import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, map, Observable, of, switchMap } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { PropsOf } from '@blaze-runtime/engine-domain';
import { getActiveBinding, handleCustomFieldsSchema } from '@blaze-shared/elements-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { findColumn } from '@blaze-shared/schema-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import { ElementDefinitionsService } from '../../../../services/element-definitions.service';
import { ElementStoreViewService } from '../../../../services/element-store-view.service';
import { getFormElementCustomFields } from '../../../../utils/elements/form.utils';
import { hasDataProps, hasProps } from '../../../element-model/element-props-guards';
import { DataProviderQuery, InputDataPackage } from '../../io-data.models';

import { DataSourceProvider } from './data-source.data-provider';
import { createColumnValueSchema } from './utils/schema.utils';

@Injectable({ providedIn: 'root' })
export class FormContainerDataProvider implements DataSourceProvider {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly schemaService = inject(SchemaService);
  private readonly elementStoreView = inject(ElementStoreViewService);
  private readonly store = inject(Store);
  readonly sourceType = 'formContainer' as const;

  getDataStream({ source, bindings, element }: DataProviderQuery): Observable<InputDataPackage> {
    const dataSourceId = source.id;
    if (!bindings) {
      throw new Error('Form Container binding missing');
    }

    const distinctFn =
      element.type !== 'signature'
        ? isDeepEqual
        : (previous: any, current: any): boolean => current !== null && previous === current;
    const formContainerDefaultOutput =
      this.elementDefinitionsService.getDefaultOutputEventType('formContainer');

    return this.store
      .select(runtimeSelectors.selectOutputUiById(dataSourceId, formContainerDefaultOutput))
      .pipe(
        concatLatestFrom(() =>
          this.elementStoreView.getElement(dataSourceId).pipe(
            switchMap((formElement) => {
              if (!hasDataProps(formElement) || !hasProps<PropsOf<'formContainer'>>(formElement)) {
                return of(undefined);
              }

              const formDataSource = getActiveBinding(formElement.props.data, 'value')?.dataSource;
              if (!formDataSource) {
                return of(undefined);
              }

              const isCustomForm = formElement.props.content?.action === 'custom';
              if (isCustomForm) {
                const valueSchema = syncSelect(
                  this.store,
                  runtimeSelectors.selectSchemaById(dataSourceId, formContainerDefaultOutput),
                );
                const schema = handleCustomFieldsSchema(
                  valueSchema,
                  getFormElementCustomFields(formElement.props?.content.model),
                );
                return of(schema);
              }
              if (
                formDataSource.sourceType === 'dataset' ||
                formDataSource.sourceType === 'formGroup'
              ) {
                return of(this.schemaService.getSchema(formDataSource.id));
              }
              return this.store
                .select(runtimeSelectors.selectWorkflowsSchemaById(formDataSource.id))
                .pipe(
                  map((schema) => {
                    if (schema && !schema.columns.length && formDataSource.columns) {
                      schema = { ...schema, columns: formDataSource.columns };
                    }
                    return schema;
                  }),
                );
            }),
          ),
        ),
        map(([output, schema]) => {
          const columnId = Object.values(bindings)[0];
          const field = this.schemaService.getColumnName(columnId);
          const data =
            field && output?.data ? ((output.data[field] ?? null) as unknown) : undefined;
          const column = schema ? findColumn(schema.columns, columnId) : undefined;
          return { data, updateSource: output?.updateSource, schema, column };
        }),
        distinctUntilChanged(distinctFn),
        filter((output, idx) => {
          const { updateSource, column, data } = output;
          const isNotFirstEmission = idx > 0;

          // Exclude if the data is undefined
          if (data === undefined) {
            return false;
          }

          // Exclude subsequent 'form-input' update events
          if (isNotFirstEmission && updateSource?.startsWith('form-input')) {
            return false;
          }

          // Exclude subsequent 'formulas-refresh' update events for columns that are not formulas or computed
          const isFormulaRefresh = updateSource?.startsWith('formulas-refresh');
          const shouldNotRefreshColumn =
            column &&
            (!(!!column.formula || column.isComputed || column.isInline) || column.isInternal);
          if (isNotFirstEmission && isFormulaRefresh && shouldNotRefreshColumn) {
            return false;
          }

          return true;
        }),
        map(
          ({ data, updateSource, schema, column }) =>
            <InputDataPackage>{
              sourceType: 'formContainer',
              data,
              // Schema describing the single bound field value rather than the whole form data source
              schema: column ? createColumnValueSchema(column) : schema,
              column,
              updateSource,
            },
        ),
      );
  }
}
